import { InputGroup, InputLeftElement, type InputProps } from '@chakra-ui/react'
import { Box, Input as ChakraInput, FormControl, FormErrorMessage, FormLabel, InputRightElement, Text, forwardRef } from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { BaseTooltip } from './Tooltip'
import { FlexRow } from './flex'

export interface IProps {
  label?: string
  labelFontSize?: string
  labelFontColor?: string
  labelFontWeight?: string
  labelJustifyContent?: 'flex-start' | 'center' | 'flex-end' | 'space-between'
  errorMessage?: string
  showInfo?: boolean
  helperText?: string
  subtitle?: string
  IconRight?: ReactNode
  ButtonRight?: ReactNode
  ButtonLeft?: ReactNode
  IconLeft?: ReactNode
  appendOutside?: string
  labelRight?: string
  labelRightFontSize?: string
  labelRightFontWeight?: string | number
  labelRightColor?: string
  paddingLeft?: string
  paddingRight?: string
  blackInfoIcon?: boolean
  maxLength?: number
  adjustFormWidth?: boolean
}

export const BaseInput = forwardRef<InputProps & IProps, 'input'>(
  (
    {
      label,
      labelFontSize,
      labelFontColor,
      errorMessage,
      showInfo = false,
      helperText,
      subtitle,
      IconRight,
      ButtonRight,
      ButtonLeft,
      IconLeft,
      labelRight,
      labelRightFontSize,
      labelRightFontWeight,
      labelRightColor,
      appendOutside,
      paddingLeft,
      paddingRight,
      blackInfoIcon,
      maxLength,
      labelFontWeight,
      labelJustifyContent,
      isRequired,
      adjustFormWidth,
      ...props
    },
    ref
  ) => {
    return (
      <FormControl isInvalid={props.isInvalid} isRequired={isRequired} width={adjustFormWidth ? props.width : undefined}>
        {label && (
          <FlexRow justifyContent={'flex-start'} marginBottom={2} alignItems='center' paddingRight={'8px'}>
            <FormLabel fontWeight={labelFontWeight} fontSize={labelFontSize} marginBottom={0} color={labelFontColor}>
              {label}
            </FormLabel>
            {showInfo && <BaseTooltip blackIcon={blackInfoIcon} width={'16px'} height={'16px'} label={helperText} />}
          </FlexRow>
        )}
        {subtitle && (
          <Text fontSize={'14px'} color={'#848484'} fontFamily={'Parabole'} marginTop={'-14px'} marginBottom={'8px'}>
            {subtitle}
          </Text>
        )}

        <FlexRow alignItems={'center'}>
          <InputGroup variant='unstyled' display='flex' alignItems='center'>
            {IconLeft && (
              <InputLeftElement pointerEvents='none' top='unset' paddingRight={5} h='full'>
                {IconLeft}
              </InputLeftElement>
            )}
            {ButtonLeft && (
              <InputLeftElement top='unset' paddingLeft={3} h='full'>
                {ButtonLeft}
              </InputLeftElement>
            )}

            <ChakraInput
              paddingLeft={IconLeft ? 8 : paddingLeft ?? undefined}
              paddingRight={ButtonRight ? 12 : paddingRight ?? undefined}
              paddingTop={'3px'}
              paddingBottom={0}
              verticalAlign={'center'}
              height={'32px'}
              maxHeight={'48px'}
              placeholder=''
              maxLength={maxLength}
              border='1px solid border'
              backgroundColor={'background'}
              {...props}
              ref={ref}
            />
            {appendOutside && appendOutside.length > 0 && <Box marginLeft={'8px'}>{appendOutside}</Box>}
            {IconRight && (
              <InputRightElement h='full' top='unset' pointerEvents='none' paddingRight={3}>
                {IconRight}
              </InputRightElement>
            )}
            {ButtonRight && (
              <InputRightElement h='full' top='unset' paddingRight={3} paddingLeft={'24px'}>
                {ButtonRight}
              </InputRightElement>
            )}
            {labelRight && (
              <InputRightElement pointerEvents='none' top='unset' h='full' display='flex' alignItems='center' paddingRight={3}>
                <Text fontWeight={labelRightFontWeight ?? 400} fontSize={labelRightFontSize ?? '16px'} color={labelRightColor ?? '#656565'}>
                  {labelRight}
                </Text>
              </InputRightElement>
            )}
          </InputGroup>
        </FlexRow>
        {props.isInvalid && errorMessage ? (
          <FormErrorMessage position='absolute' bottom='-6' top='auto' fontSize='12px'>
            {errorMessage}
          </FormErrorMessage>
        ) : null}
      </FormControl>
    )
  }
)
